import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import Img from '../components/ui/Img';
import { timeline, values, areas, foundation } from '../data';
import { IcoArrow, IcoCheck, IcoTarget, IcoEye, LogoMark } from '../assets/icons';

function PageHero({ isPt }) {
  return (
    <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
      <div className="absolute inset-0">
        <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9456_1.JPG" alt="Fundação" className="w-full h-full" style={{ objectFit: 'cover', opacity: 0.15 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-950/50 via-brand-950/80 to-brand-950" />
      </div>
      <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
          <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
            {isPt ? 'Criada a 20 de Fevereiro de 2020' : 'Created on 20 February 2020'}
          </span>
        </div>
        <h1 className="display-xl text-white mb-6">{isPt ? 'Quem Somos' : 'About Us'}</h1>
        <p className="body-lg text-brand-400 max-w-xl mx-auto">
          {isPt
            ? 'Uma organização filantrópica angolana comprometida com o desenvolvimento sustentável das comunidades mais vulneráveis do país.'
            : 'An Angolan philanthropic organisation committed to the sustainable development of the country\'s most vulnerable communities.'}
        </p>
      </div>
    </section>
  );
}

function HistorySection({ isPt }) {
  const refL = useAppear('appear-left');
  const refR = useAppear();
  return (
    <section id="historia" className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap grid lg:grid-cols-2 gap-16 items-center">
        <div ref={refL}>
          
          <h2 className="display-lg text-brand-900 dark:text-white mb-6">
            {isPt ? 'Nascemos para servir Angola.' : 'Born to serve Angola.'}
          </h2>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-5">
            {isPt
              ? 'A Fundação Ngana Zenza para o Desenvolvimento Comunitário é uma organização filantrópica angolana criada a 20 de Fevereiro de 2020, pela Primeira-Dama da República de Angola, Ana Dias Lourenço, com o objectivo de promover o desenvolvimento sustentável das comunidades mais vulneráveis do país.'
              : 'The Ngana Zenza Foundation for Community Development is an Angolan philanthropic organisation created on 20 February 2020, by the First Lady of the Republic of Angola, Ana Dias Lourenço, with the aim of promoting sustainable development of the country\'s most vulnerable communities.'}
          </p>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-5">
            {isPt
              ? 'A Fundação actua nas áreas da saúde, educação, desenvolvimento comunitário e rural, cidadania, inclusão social, sustentabilidade e assistência humanitária.'
              : 'The Foundation operates in the areas of health, education, community and rural development, citizenship, social inclusion, sustainability and humanitarian assistance.'}
          </p>
          <p className="body-md text-brand-700 dark:text-brand-400">
            {isPt
              ? 'O nome "Ngana Zenza" homenageia Isabel "Ngana Zenza", mãe da Primeira-Dama, símbolo de solidariedade, humanidade e compromisso com o próximo.'
              : 'The name "Ngana Zenza" honours Isabel "Ngana Zenza", mother of the First Lady a symbol of solidarity, humanity and commitment to others.'}
          </p>
        </div>
        <div ref={refR}>
          <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/LJB.jpg" alt="Fundação Ngana Zenza" className="w-full h-[480px] rounded-3xl" />
        </div>
      </div>
    </section>
  );
}

function MissionVisionSection({ isPt }) {
  const ref = useAppear();
  return (
    <section id="missao" className="section-pad bg-brand-50 dark:bg-brand-900/20">
      <div className="wrap">
        <div ref={ref} className="grid lg:grid-cols-2 gap-6 mb-16">
          {/* Mission */}
          <div className="card-orange p-10">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-6">
              <IcoTarget cls="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {isPt ? 'Missão' : 'Mission'}
            </h3>
            <p className="text-brand-100 leading-relaxed">
              {isPt ? foundation.mission_pt : foundation.mission_en}
            </p>
          </div>

          {/* Vision */}
          <div className="card-base p-10">
            <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-900 flex items-center justify-center mb-6 border border-brand-100 dark:border-brand-800">
              <IcoEye cls="w-6 h-6 text-brand-600" />
            </div>
            <h3 className="text-xl font-bold text-brand-900 dark:text-white mb-4">
              {isPt ? 'Visão' : 'Vision'}
            </h3>
            <p className="text-brand-600 dark:text-brand-400 leading-relaxed">
              {isPt ? foundation.vision_pt : foundation.vision_en}
            </p>
          </div>
        </div>

        {/* Values */}
        <div id="valores">
          <p className="label-tag justify-center text-center mb-10">
            {isPt ? 'Os Nossos Valores' : 'Our Values'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {values.map((v, i) => (
              <div key={i} className="card-base p-5 text-center hover:-translate-y-1 transition-transform group">
                <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900 border border-brand-100 dark:border-brand-800 flex items-center justify-center mx-auto mb-3 group-hover:bg-brand-600 group-hover:border-brand-600 transition-colors">
                  <IcoCheck cls="w-4 h-4 text-brand-600 group-hover:text-white transition-colors" />
                </div>
                <p className="font-semibold text-brand-900 dark:text-white text-sm">
                  {isPt ? v.pt : v.en}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AreasSection({ isPt }) {
  const ref = useAppear();
  return (
    <section className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap">
        <div ref={ref} className="text-center mb-12">
          <p className="label-tag justify-center">{isPt ? 'Áreas de Actuação' : 'Areas of Action'}</p>
          <h2 className="display-md text-brand-900 dark:text-white">
            {isPt ? '11 Áreas de Intervenção' : '11 Areas of Intervention'}
          </h2>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          {areas.map((a, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/40 hover:border-brand-500 hover:bg-brand-100 dark:hover:bg-brand-900 transition-all cursor-default"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-600 flex-shrink-0" />
              <span className="text-sm font-medium text-brand-700 dark:text-brand-400">
                {isPt ? a.pt : a.en}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FounderSection({ isPt }) {
  const refL = useAppear();
  const refR = useAppear('appear-left');
  return (
    <section id="fundadora" className="section-pad bg-brand-50 dark:bg-brand-900/20">
      <div className="wrap grid lg:grid-cols-2 gap-16 items-center">
        <div ref={refL}>
          <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/24.jpg" alt="Ana Dias Lourenço" className="w-full h-[520px] rounded-3xl" />
        </div>
        <div ref={refR}>
          <p className="label-tag">{isPt ? 'A Fundadora' : 'The Founder'}</p>
          <h2 className="text-3xl font-bold text-brand-900 dark:text-white mb-2">
            Ana Dias Lourenço
          </h2>
          <p className="text-brand-600 font-semibold mb-7 text-[15px]">
            {isPt
              ? 'Fundadora e Presidente de Honra\nPrimeira-Dama da República de Angola'
              : 'Founder and Honorary President\nFirst Lady of the Republic of Angola'}
          </p>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-5">
            {isPt
              ? 'Ana Dias Lourenço é a Primeira-Dama da República de Angola e fundou a Fundação Ngana Zenza a 20 de Fevereiro de 2020, impulsionada pelo desejo de promover o desenvolvimento humano e comunitário nas populações mais vulneráveis do país.'
              : 'Ana Dias Lourenço is the First Lady of the Republic of Angola and founded the Ngana Zenza Foundation on 20 February 2020, driven by the desire to promote human and community development in the country\'s most vulnerable populations.'}
          </p>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-8">
            {isPt
              ? 'A Fundação leva o nome da sua mãe, Isabel "Ngana Zenza" símbolo de solidariedade, humanismo e compromisso com o próximo. Estes são os valores que orientam toda a missão da organização.'
              : 'The Foundation carries the name of her mother, Isabel "Ngana Zenza" a symbol of solidarity, humanism and commitment to others. These are the values that guide the entire mission of the organisation.'}
          </p>

          {/* Quote */}
          <blockquote className="border-l-4 border-brand-600 pl-6 py-1">
            <p className="italic text-brand-700 dark:text-brand-300 leading-relaxed">
              {isPt
                ? '"A Fundação Ngana Zenza nasceu do desejo profundo de contribuir para que nenhum angolano fique para trás."'
                : '"The Ngana Zenza Foundation was born from a deep desire to ensure no Angolan is left behind."'}
            </p>
            <cite className="text-sm text-brand-500 not-italic mt-2 block">— Ana Dias Lourenço</cite>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function TimelineSection({ isPt }) {
  const ref = useAppear();
  return (
    <section className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap">
        <div ref={ref} className="text-center mb-16">
          <p className="label-tag justify-center">{isPt ? 'Cronologia' : 'Timeline'}</p>
          <h2 className="display-md text-brand-900 dark:text-white">
            {isPt ? 'Cinco Anos de Transformação' : 'Five Years of Transformation'}
          </h2>
        </div>
        <div className="relative max-w-3xl mx-auto">
          {/* Vertical line */}
          <div className="absolute left-[23px] top-2 bottom-2 w-px bg-brand-100 dark:bg-brand-900" />

          <div className="space-y-8">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-8 group">
                {/* Dot */}
                <div className="relative flex-shrink-0">
                  <div className="w-[48px] h-[48px] rounded-full border-2 border-brand-200 dark:border-brand-800 bg-white dark:bg-brand-950 flex items-center justify-center group-hover:border-brand-600 group-hover:bg-brand-50 dark:group-hover:bg-brand-900 transition-all">
                    <span className="text-[11px] font-bold text-brand-600 dark:text-brand-500">
                      {item.year.slice(2)}
                    </span>
                  </div>
                </div>
                {/* Content */}
                <div className="flex-1 pt-1 pb-4">
                  <span className="text-[11px] font-bold text-brand-500 uppercase tracking-widest">{item.year}</span>
                  <h4 className="font-bold text-brand-900 dark:text-white text-[15px] mt-0.5 mb-1.5">
                    {isPt ? item.title_pt : item.title_en}
                  </h4>
                  <p className="text-sm text-brand-600 dark:text-brand-500 leading-relaxed">
                    {isPt ? item.desc_pt : item.desc_en}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  return (
    <main>
      <PageHero isPt={isPt} />
      <HistorySection isPt={isPt} />
      <MissionVisionSection isPt={isPt} />
      <AreasSection isPt={isPt} />
      <FounderSection isPt={isPt} />
      <TimelineSection isPt={isPt} />
    </main>
  );
}
